'use client';

import { useCallback, useEffect, useRef } from 'react';

/**
 * Ignore focus events landing within this window of the last pull, so ordinary
 * alt-tabbing mid-edit cannot spam the Graph stat and the Gotenberg render
 * behind it. Load-bearing rather than a nicety: a lawyer working between Word
 * and Vale crosses this listener dozens of times in a sitting.
 */
export const WORD_FOCUS_PULL_DEBOUNCE_MS = 5_000;

/**
 * "I'm back from Word" — run the checkout-aware read seam when the app regains
 * focus, rather than waiting on the ~30s change notification (and, wherever
 * there is no public webhook URL, waiting forever).
 *
 * Both events matter and neither covers the other: `visibilitychange` fires on
 * returning from the Word Online *tab*, `focus` on returning from desktop Word
 * or another window. The visibility check inside the handler is what stops a
 * background `focus` from pulling for a tab nobody is looking at.
 *
 * `armed` is the whole gate, and it should be the narrowest truthful signal the
 * host has: a live checkout where the surface knows about one, else the local
 * "this panel launched Word" intent. Left permanently on, the listener would
 * pull for documents nobody has opened (VALE-288).
 */
export function useWordFocusPull({
  armed,
  onPull,
  debounceMs = WORD_FOCUS_PULL_DEBOUNCE_MS
}: {
  armed: boolean;
  /** Must be stable, or the listener is torn down and rebuilt every render. */
  onPull: () => void;
  debounceMs?: number;
}): { notePulled: () => void } {
  // A ref rather than state: the debounce window must not re-run the effect,
  // and a re-arm mid-session should not reopen it.
  const lastPullAtRef = useRef(0);

  /**
   * Restart the debounce window for a pull the host performed itself.
   *
   * The template preview pane needs this: its read seam runs *inside* the
   * preview render, so an initial or version-change render has already paid for
   * the round trip. Without it, opening a drawer and immediately alt-tabbing
   * back re-renders for identical bytes.
   */
  const notePulled = useCallback(() => {
    lastPullAtRef.current = Date.now();
  }, []);

  useEffect(() => {
    if (!armed) return;
    const onRegainFocus = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastPullAtRef.current < debounceMs) return;
      lastPullAtRef.current = now;
      onPull();
    };
    window.addEventListener('focus', onRegainFocus);
    document.addEventListener('visibilitychange', onRegainFocus);
    return () => {
      window.removeEventListener('focus', onRegainFocus);
      document.removeEventListener('visibilitychange', onRegainFocus);
    };
  }, [armed, onPull, debounceMs]);

  return { notePulled };
}
